'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { trackButtonClick, trackAssetDownload } from '@/components/Analytics';
import { LeadCaptureForm } from '@/components/LeadCaptureForm';
import { LEAD_DOWNLOADS_ID } from '@/lib/scroll-targets';
import { ClaimAccountPrompt } from '@/components/ClaimAccountPrompt';

/**
 * The list is FREE but SIGN-IN GATED, and the PDF is firm-level only.
 *
 * This page used to collect an email and hand back a link to
 * public/free-vc-list/50-active-vcs.pdf — a static file anyone could fetch
 * directly, containing 50 named individuals and their personal work addresses.
 * The form gated nothing; the URL was simply guessable.
 *
 * The email form is gone rather than kept alongside the gate: its promise
 * ("we'll email you the PDF") is one we can no longer keep, and collecting an
 * address in exchange for a download that then demands a login would be a dark
 * pattern. Signing up captures the same address, verified.
 */

type AuthState = 'checking' | 'anonymous' | 'authenticated';

const DOWNLOAD_PATH = '/api/free-vc-list/download';

export function FreeVcListContent() {
  const { t } = useTranslation();
  // Localized value props (pageContent.freeVcList.valueBullets). returnObjects
  // gives the array; guard with a fallback in case a locale is mid-deploy.
  const valueBullets = t('pageContent.freeVcList.valueBullets', {
    returnObjects: true,
  }) as string[];
  const bullets = Array.isArray(valueBullets) ? valueBullets : [];

  const [authState, setAuthState] = useState<AuthState>('checking');
  // A visitor who confirmed their address by opening the mailbox holds the lead
  // cookie, which /api/free-vc-list/download accepts in place of a session.
  // The cookie is httpOnly, so the answer comes back from the session probe.
  const [leadVerified, setLeadVerified] = useState(false);
  const [cameFromGate, setCameFromGate] = useState(false);

  useEffect(() => {
    // The download route bounces unauthenticated browser navigations here with
    // ?auth=required so the sign-in ask has context instead of appearing at
    // random. Read it from the URL directly — useSearchParams() would opt this
    // route out of static rendering.
    if (typeof window !== 'undefined') {
      setCameFromGate(new URLSearchParams(window.location.search).get('auth') === 'required');
    }

    let cancelled = false;
    // Same probe the Header uses: 200 + { authenticated } either way, so a
    // signed-out visitor doesn't get a console error on a public page.
    fetch('/api/auth/check-session', { credentials: 'include', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setAuthState(data?.authenticated === true ? 'authenticated' : 'anonymous');
        setLeadVerified(data?.leadVerified === true);
      })
      .catch(() => {
        // Fail closed: show the sign-in prompt rather than a download button
        // that would just bounce them back here.
        if (!cancelled) setAuthState('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    // This page renders inside PersistentLayout, which already provides the
    // single <main> landmark and the global <header> with the SendVC logo link.
    // A plain <div> here keeps exactly one <main> and one <header> per document,
    // so main-content extraction (assistive tech, reader modes) still works.
    // pt-6 replaces the vertical spacing the removed local header contributed.
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <section className="container pt-6 pb-16">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-amber-100 text-amber-900 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
              {t('pageContent.freeVcList.badge')}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('pageContent.freeVcList.hero.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('pageContent.freeVcList.hero.subtitle')}
            </p>
          </div>

          <ul className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-6 mb-8">
            {bullets.map((bullet) => (
              <li
                key={bullet}
                className="inline-flex items-center justify-center gap-2 text-sm font-medium text-gray-700"
              >
                <svg
                  className="w-5 h-5 flex-shrink-0 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {bullet}
              </li>
            ))}
          </ul>

          {cameFromGate && authState === 'anonymous' && (
            <div
              role="status"
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              {t('pageContent.freeVcList.gate.authRequiredNotice')}
            </div>
          )}

          {/* Scroll target for LeadConfirmed: confirming lands at the top of the
              page and the list is several screens down, so the click would
              otherwise read as "nothing happened". scroll-mt keeps the sticky
              header off it. */}
          <div
            id={LEAD_DOWNLOADS_ID}
            className="scroll-mt-24 bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100 text-center"
          >
            {authState === 'checking' ? (
              <p className="text-gray-500 py-8">{t('pageContent.freeVcList.gate.checking')}</p>
            ) : authState === 'authenticated' || leadVerified ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('pageContent.freeVcList.ready.title')}
                </h2>
                <p className="text-gray-600 mb-6">{t('pageContent.freeVcList.ready.body')}</p>
                {/* A plain link, not fetch+blob: the route sets
                    Content-Disposition: attachment and the session cookie rides
                    along on a same-origin navigation. */}
                {/* NO trackLeadCapture() here. It used to fire on this click,
                    because with the sign-in gate in place a signed-in visitor
                    taking the download was the closest thing to an "email
                    captured" event the site had. The lead conversion now fires
                    once, on the confirmation landing (components/LeadConfirmed),
                    which is both earlier and better evidence — it means the
                    address is real. Leaving it here too would double-count every
                    confirmed visitor, since this branch now renders for them as
                    well as for signed-in users. file_download still fires; it is
                    a different event and it is not the conversion. */}
                <a
                  href={DOWNLOAD_PATH}
                  className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                  onClick={() => trackAssetDownload('free-vc-list', 'free_vc_list_page', true, 'pdf')}
                >
                  {t('pageContent.freeVcList.ready.downloadCta')}
                </a>
              </>
            ) : (
              /* ANONYMOUS: email capture, NOT the sign-in wall this used to be.
                 Read the header comment before changing this. The original email
                 form was removed because it collected an address and then handed
                 back a permanent public URL to a PDF of NAMED INDIVIDUALS. Two
                 things changed since: the PDF is entity-only (firms, never
                 people — see scripts/vc-list-build-data.mjs), and the download is
                 a gated route rather than a file under public/.
                 So the objection that stands is the dark pattern — asking for an
                 email and THEN demanding a login. This form does not do that: it
                 REPLACES the login. Confirming the address sets a signed lead
                 cookie (lib/lead-session.ts) that opens the gated route without
                 a session, so nothing becomes permanently public and nobody is
                 asked to pay twice for the same download. */
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('pageContent.freeVcList.gate.title')}
                </h2>
                <p className="text-gray-600 mb-6">{t('pageContent.freeVcList.gate.body')}</p>
                <LeadCaptureForm
                  source="free-vc-list"
                  cta={t('pageContent.leadCapture.freeVcList.cta', {
                    defaultValue: 'Send me the list',
                  })}
                  blurb={t('pageContent.leadCapture.freeVcList.blurb', {
                    defaultValue:
                      "50 active VC firms — stage, focus, check size, and each firm's own contact route. Firm-level only; no personal contact details.",
                  })}
                />
                <p className="mt-4 text-sm text-gray-500">
                  Already have an account?{' '}
                  <Link href="/login?redirect=/free-vc-list" className="text-primary-600 hover:text-primary-700 font-medium">
                    {t('pageContent.freeVcList.gate.signInCta')}
                  </Link>
                </p>
              </>
            )}

            <ClaimAccountPrompt
            show={authState === 'anonymous' && leadVerified}
            source="free-vc-list"
          />

          {/* Free→paid bridge: the list is the hook; the done-for-you
                outreach is the product. A styled card (not a text link)
                converts materially better here. */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="rounded-xl bg-primary-50 border border-primary-100 p-5 text-left">
                <p className="text-sm text-gray-700 mb-4">
                  {t('pageContent.freeVcList.upgrade.intro')}
                </p>
                {/* The ?utm_source=lead_magnet&utm_medium=thank_you this link used to
                    carry restarted the GA session, wiping the original acquisition
                    source; lead_magnet → thank_you provenance rides on the click event. */}
                <Link
                  href="/pricing?billing=yearly"
                  className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
                  onClick={() => trackButtonClick('lead_magnet_thank_you_pricing_cta')}
                >
                  {t('pageContent.freeVcList.upgrade.cta')}
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-600 text-left">
                {t('pageContent.freeVcList.next.prefix')}{' '}
                <Link href="/pitch-deck-templates" className="text-primary-600 hover:text-primary-700 underline">
                  {t('pageContent.freeVcList.next.templates')}
                </Link>
                ,{' '}
                <Link href="/blog/writing-executive-summary" className="text-primary-600 hover:text-primary-700 underline">
                  {t('pageContent.freeVcList.next.summary')}
                </Link>
                , or{' '}
                <Link href="/blog/find-investors-for-seed-round" className="text-primary-600 hover:text-primary-700 underline">
                  {t('pageContent.freeVcList.next.seed')}
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
